import React, { useMemo, useState, useEffect, useContext, useCallback } from "react";
import { Box, Typography, Stack, Divider, CircularProgress } from "@mui/material";
import { KeyboardArrowRight as ArrowRightIcon } from "@mui/icons-material";
import { WidgetContext } from "../../store/widgetContext";
import { get } from "lodash";
// Phase-4 §4.4: these reads 404'd against WAS since the knowledge extraction — the docs domain lives on the knowledge service now.
import { axiosKnowledge } from "../../configs/http-config";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

export default function Articles({ handleNavigate, setSelectedDocument, setSelectedCollection, searchQuery, selectedCollection, uuid, botId, tenantId }) {
	const { widget, hostName, pathName } = useContext(WidgetContext);
	const faqsDocs = get(widget, "pages.faqs.docs", []);
	const [articles, setArticles] = useState([]);
	const [subCollections, setSubCollections] = useState([]);
	const [loading, setLoading] = useState(false);
	const collections = useMemo(() => faqsDocs.map(item => item.doc).filter(Boolean), [faqsDocs]);
	const { msg } = useWidgetMsg();

	const fetchArticles = useCallback(async (folderIds) => {
		if (!folderIds || !Array.isArray(folderIds) || folderIds.length === 0) return;
		setLoading(true);
		try {
			const response = await axiosKnowledge.post('/documents/public/list', { folderIds, limit: 100, page: 1 }, {
				params: { uuid, botId, tenantId }, headers: { "X-Host": hostName, "X-Pathname": pathName }
			});

			if (response.status === 200) {
				const data = get(response, "data.data", {});
				const articlesList = Array.isArray(data.data) ? data.data : [];
				setArticles(articlesList);
			}
		} catch (error) {
			console.error("Error fetching articles:", error);
			setArticles([]);
		} finally {
			setLoading(false);
		}
	}, [uuid, botId, tenantId, hostName, pathName]);

	const fetchSubCollections = useCallback(async (collectionId) => {
		if (!collectionId) return;
		setLoading(true);
		try {
			const response = await axiosKnowledge.post('/folders/public/list', { collectionId, limit: 100, page: 1 }, {
				params: { uuid, botId, tenantId }, headers: { "X-Host": hostName, "X-Pathname": pathName }
			});

			if (response.status === 200) {
				const data = get(response, "data.data", {});
				const subCollectionsList = Array.isArray(data.data) ? data.data : [];
				setSubCollections(subCollectionsList);
			}
		} catch (error) {
			console.error("Error fetching subcollections:", error);
			setSubCollections([]);
		} finally {
			setLoading(false);
		}
	}, [uuid, botId, tenantId, hostName, pathName]);

	useEffect(() => {
		if (!selectedCollection?._id) {
			setArticles([]);
			setSubCollections([]);
			return;
		}

		const subCollectionCount = selectedCollection.subCollectionCount ?? 0;

		if (subCollectionCount > 0) {
			fetchSubCollections(selectedCollection._id);
		} else {
			setSubCollections([]);
		}
		fetchArticles([selectedCollection._id]);
	}, [selectedCollection, fetchArticles, fetchSubCollections]);

	const displayData = useMemo(() => {
		if (selectedCollection) {
			const subCollectionCount = selectedCollection.subCollectionCount ?? 0;

			if (subCollectionCount > 0) {
				let filteredSubCollections = subCollections;
				if (searchQuery.trim()) {
					filteredSubCollections = subCollections.filter(subCol => subCol.title?.toLowerCase().includes(searchQuery.toLowerCase()));
				}
				let filteredArticles = articles;
				if (searchQuery.trim()) {
					filteredArticles = articles.filter(article => article.title?.toLowerCase().includes(searchQuery.toLowerCase()));
				}
				return { collections: filteredSubCollections, articles: filteredArticles };
			} else {
				let filteredArticles = articles;
				if (searchQuery.trim()) {
					filteredArticles = articles.filter(article => article.title?.toLowerCase().includes(searchQuery.toLowerCase()));
				}
				return { collections: [], articles: filteredArticles };
			}
		}

		const filtered = searchQuery.trim()
			? collections.filter(collection => collection.title?.toLowerCase().includes(searchQuery.toLowerCase()))
			: collections;

		return { collections: filtered, articles: [] };
	}, [collections, selectedCollection, searchQuery, articles, subCollections]);

	const handleSubCollectionClick = (subCollection, event) => {
		if (event) {
			event.stopPropagation();
		}
		if (setSelectedCollection && handleNavigate) {
			setSelectedCollection({ ...subCollection, parentCollection: selectedCollection });
			handleNavigate({ location: "collection" });
		}
	};

	const handleCollectionClick = (collection, event) => {
		if (event) {
			event.stopPropagation();
		}
		if (collection.parentId) {
			return handleSubCollectionClick(collection, event);
		}
		if (setSelectedCollection && handleNavigate) {
			setSelectedCollection(collection);
			handleNavigate({ location: "collection" });
		}
	};

	const handleArticleClick = (article, event) => {
		if (event) {
			event.stopPropagation();
		}
		if (article && setSelectedDocument && handleNavigate) {
			setSelectedDocument({ ...article, previousCollection: selectedCollection });
			handleNavigate({ location: "document" });
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				borderRadius: "16px",
				overflow: "hidden",
			}}
		>
			<Box sx={{ flex: 1, overflowY: "auto", padding: "20px 20px 60px 20px", background: "white" }}>
				{loading ? (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "200px", textAlign: "center" }}>
						<CircularProgress size={24} />
					</Box>
				) : (
					<>
						{displayData.collections.length > 0 && (
							<Stack spacing={0}>
								{displayData.collections.map((collection, index) => (
									<Box key={collection._id}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												py: 0,
												cursor: "pointer",
												"&:hover": { opacity: 0.7 },
											}}
											onClick={(e) => handleCollectionClick(collection, e)}
										>
											<Box>
												<Typography variant="body2" sx={{ fontSize: "14px", color: "black" }}>
													{collection.title}
												</Typography>
											</Box>
											<ArrowRightIcon sx={{ fontSize: 16, color: "black", mb: 0.5 }} />
										</Box>
										{index < displayData.collections.length - 1 && (
											<Divider sx={{ borderColor: "#E5E5E5", my: 1 }} />
										)}
									</Box>
								))}
							</Stack>
						)}
						{displayData.collections.length > 0 && displayData.articles.length > 0 && <Divider sx={{ borderColor: "#E5E5E5", my: 1 }} />}
						{displayData.articles.length > 0 && (
							<Stack spacing={0}>
								{displayData.articles.map((article, index) => (
									<Box key={article._id || index}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												py: 0,
												cursor: "pointer",
												"&:hover": { opacity: 0.7 },
											}}
											onClick={(e) => handleArticleClick(article, e)}
										>
											<Typography variant="body2" sx={{ fontSize: "14px", color: "black" }}>
												{article.title}
											</Typography>
											<ArrowRightIcon sx={{ fontSize: 16, color: "black", mb: 0.5 }} />
										</Box>
										{index < displayData.articles.length - 1 && (
											<Divider sx={{ borderColor: "#E5E5E5", my: 1 }} />
										)}
									</Box>
								))}
							</Stack>
						)}
						{displayData.collections.length === 0 && displayData.articles.length === 0 &&
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "200px",
									textAlign: "center",
								}}
							>
								<Typography variant="body2" sx={{ fontSize: "14px", color: "#808080" }}>
									{msg("help.no_collections_found", "No collections found")}
								</Typography>
							</Box>}
					</>
				)}
			</Box>
		</Box>
	);
}

